import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import anime from 'animejs';
import Button from 'sushiweb/Button';
import H1 from 'sushiweb/Typography/Heading/H1';
import H2 from 'sushiweb/Typography/Heading/H2';
import H4 from 'sushiweb/Typography/Heading/H4';
import { useLocation } from 'react-router-dom';
import Layout from 'sushiweb/Layout';
import CheckCircleFill from 'sushiweb/Icons/all/CheckCircleFill';
import green from 'sushiweb/tokens/color/green';
import Carousel from 'sushiweb/Carousel';
import { clearCookie, sha1Encode } from '../../helpers';
import zfetch, { METHOD_POST } from '../common/utils/zfetch';
import { GET_AUTH_VALIDATE } from '../../constants/urls';
import {
  CarouselContainer,
  CarouselDesktopWrapper,
  CarouselMobileWrapper,
  CheersFoldContainer,
  CheersSlideContainer,
  CheersSlideContent,
  CheersSlideFooter,
  CheersSlideImageContainer,
  CheersSlideSign,
  CheersSlideSignLeft,
  CheersSlideSignRight,
  ConnectButton,
  ConnectNowButton,
  ConnectStickyButton,
  CrownContainer,
  DashboardViewSVGContainer,
  ErrorContainer,
  ErrorContainerV2,
  Fold1Container,
  Fold1Content,
  Fold1DesktopWrapper,
  Fold1FormWrapper,
  Fold1SVGLeftContainer,
  Fold1SVGRightContainer,
  Fold1TextWrapper,
  Fold2Cards,
  Fold2CardsContainer,
  Fold2CardsMobile,
  Fold2CardsMobileContainer,
  Fold2Container,
  Fold2Heading,
  Fold3Container,
  Fold3Heading,
  Fold3HeadingMobile,
  Fold3Left,
  Fold3Right,
  Fold4Cards,
  Fold4Container,
  Fold4SVGContainer,
  Fold5Container,
  Footer,
  FooterContent,
  FooterText,
  HeaderPill,
  IconWrapper,
  InlineFormWrapper,
  InputIconWrapper,
  LeadFormLayout,
  LeadFormTitle,
  LeadSuccessWrapper,
  MarqueeWrapper,
  ModalButtonWrapper,
  ModalContainer,
  ModalInputIconWrapper,
  PageLayout,
  RedRectangle,
  SaingsSVGContainer,
  SavingsCard,
  SavingsCardContainer,
  SavingsContainer,
  SavingsHeader,
  StyledInput,
  StyledModal,
  StyledNavbar,
  StyledNavbarMobile,
  TestimonialContent,
  TestimonialLogoContainer,
  TestimonialSlide,
  TestimonialSlider,
  TestimonialSlidingContainer,
  VerticalLineSVGContainer,
  WhiteBox,
  WhyUsCard,
  WhyUsCardLeft,
  WhyUsCardRight,
  ZLogoWrapper,
  ZLogoWrapperFooter,
} from './styles';
import {
  ArrowMobileSVG,
  CommaSVG,
  CrownSVG,
  CustomerCheersData,
  DASHBOARD_SS,
  FLOW_LINE_IMG,
  Fold1SVGLeft,
  Fold1SVGRight,
  Footer1SVG,
  Footer2SVG,
  FOOTER_IMG_V2,
  footerData,
  HowItWorks,
  Input1SVG,
  Input3SVG,
  Input4SVG,
  LaughingEmojiSVG,
  leadStatusEnum,
  Savings1SVG,
  Savings2SVG,
  SavingsBGSVG,
  SMILY_IMG,
  sourceTypeEnum,
  Testimonials,
  UseCases,
  VerticalLineSVG,
  WHY_US_IMG,
  WhyUs,
  ZFELogo,
} from './constants';
import { FOOD_AT_WORK_LOGO_IMG } from '../../constants/images';
import { insertLead } from '../../api/leadGeneration';
import { tryAgainErrorMsg } from '../../constants';
import {
  isNumericOnly,
  validateLeadEmail,
  validatePhoneNumber,
} from '../../utility';
import {
  invalidPhoneErrMsg,
  invalidEmailErrMsg,
  invalidPhoneEmailErrMsg,
  EmptyFieldErrMsg,
} from '../common/components/constants';
import ZTrackingClient from '../../jumbo/ZTrackingClient';
import FadeInWrapper from './styles/WithFadeIn';
import PopOutWrapper from './styles/WithPopOut';

const Landing = ({ scrollToFold, noIndex }) => {
  const [scrolled, setScrolled] = useState(false);
  const [scrolledMobile, setScrolledMobile] = useState(false);
  const [inlineFormScrolledUp, setInlineFormScrolledUp] = useState(false);
  const [lastClickedCard, setLastClickedCard] = useState(-1);
  const [clickedCard, setClickedCard] = useState(-1);
  const [isAnimatingCard, setIsAnimatingCard] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insertLeadResponse, setInsertLeadResponse] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [leadId, setLeadId] = useState('');
  const [useCases, setUseCases] = useState([]);
  const [budget, setBudget] = useState({});
  const [modalTitle, setModalTitle] = useState('Share your details');
  const [errorMessage, setErrorMessage] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const fold2Ref = useRef(null);
  const { hash } = useLocation();

  const closeModal = () => {
    setErrorMessage('');
    setIsVisible(false);
    if (insertLeadResponse?.status === leadStatusEnum.LEAD_STATUS_COMPLETED) {
      setName('');
      setEmail('');
      setPhone('');
      setLeadId('');
      setUseCases([]);
      setBudget({});
      setInsertLeadResponse({});
    } else {
      ZTrackingClient.recordClick({
        ui_source: 'LEAD_GEN_MODAL',
        component: 'CLOSE_MODAL',
      });
    }
  };

  const closeSuccessModal = () => {
    setIsSuccessModalVisible(false);
    setName('');
    setEmail('');
    setPhone('');
    setLeadId('');
    setUseCases([]);
    setBudget({});
    setInsertLeadResponse({});
    closeModal();
  };

  useEffect(() => {
    if (scrollToFold === 2 && fold2Ref.current) {
      fold2Ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollToFold]);

  useEffect(() => {
    if (!hash) return;

    document
      .getElementById(hash.slice(1))
      ?.scrollIntoView({ behavior: 'instant' });
  }, [hash]);

  useEffect(() => {
    clearCookie('__Host-csrft').then(
      zfetch(GET_AUTH_VALIDATE, {
        method: METHOD_POST,
      })
        .then(res => res.json())
        .then(data => {
          // can check for logged in state here
          console.log(data);
        })
        .catch(err => {
          console.error(err);
        }),
    );
  }, []);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'LANDING',
      component: 'LANDING',
    });
  }, []);

  useEffect(() => {
    if (isVisible) {
      ZTrackingClient.recordImpression({
        ui_source: 'LEAD_GEN_MODAL',
        component: 'MODAL',
      });
    }
  }, [isVisible]);

  const handleConnectNowClick = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LANDING',
      component: 'CONNECT_NOW_BUTTON',
    });
    setIsVisible(true);
  };

  const handleConnectNowNavbarClick = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LANDING',
      component: 'CONNECT_NOW_NAVBAR',
    });
    setIsVisible(true);
  };

  const handleConnectNowStickyMobileButton = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LANDING',
      component: 'CONNECT_NOW_STICKY_MOBILE',
    });
    setIsVisible(true);
  };

  const handleNameChange = val => {
    setName(val);
    setNameError(false);
  };

  const handleEmailChange = val => {
    setEmail(val);
    setEmailError(false);
  };

  const handlePhoneChange = val => {
    if (val.length > 10) return;
    if (isNumericOnly(val)) setPhone(val);
    else if (val.length === 0) setPhone('');
    setPhoneError(false);
  };

  const isFormValid = () => {
    if (
      !name ||
      !name.trim() ||
      !email ||
      !email.trim() ||
      !phone ||
      !phone.trim()
    ) {
      return false;
    }
    const isEmailValid = validateLeadEmail(email);
    const isPhoneValid = validatePhoneNumber(phone) && phone.length === 10;

    if (!isEmailValid || !isPhoneValid) {
      return false;
    }

    return true;
  };

  const handleYourNameFocus = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LEAD_GEN_MODAL',
      component: 'YOUR_NAME_INPUT',
    });
  };

  const handleWorkEmailFocus = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LEAD_GEN_MODAL',
      component: 'WORK_EMAIL_INPUT',
    });
  };

  const handlePhoneNumberFocus = () => {
    ZTrackingClient.recordClick({
      ui_source: 'LEAD_GEN_MODAL',
      component: 'PHONE_NUMBER_INPUT',
    });
  };

  const handleInsertLeads = (encodedEmail, isInlineForm = false) => {
    setLoading(true);
    const useCasesPayload = [];
    if (useCases.length) {
      useCases.map(useCase => {
        return useCasesPayload.push(...useCase?.value);
      });
    }
    const queryParams = new URLSearchParams(window.location.search);
    const utmSourceParam = queryParams.get('utm_source');
    const utmCampaignParam = queryParams.get('utm_campaign');

    insertLead({
      lead_id: leadId,
      name,
      email,
      phone_number: phone,
      budget,
      use_case: useCasesPayload,
      source: sourceTypeEnum.SOURCE_TYPE_WEBSITE,
      Meta: {
        utm_source: utmSourceParam || '',
        utm_campaign: utmCampaignParam || '',
      },
    })
      .then(res => {
        const { status = false, message = tryAgainErrorMsg } = res;
        if (!status) {
          setErrorMessage(message);
          setLoading(false);
          return;
        }
        setInsertLeadResponse(res?.data);
        setLoading(false);
        if (res?.data?.status === leadStatusEnum.LEAD_STATUS_CONTACT_SAVED) {
          setLeadId(res?.data?.lead_id);
          setModalTitle('Your requirement');
        }
        if (
          isInlineForm &&
          res?.data?.status === leadStatusEnum.LEAD_STATUS_COMPLETED
        ) {
          setIsSuccessModalVisible(true);
        }
        ZTrackingClient.recordClick({
          ui_source: 'LEAD_GEN_MODAL',
          component:
            res?.data?.status === leadStatusEnum.LEAD_STATUS_CONTACT_SAVED
              ? 'NEXT_BUTTON_SUCCESS'
              : 'SUBMIT_BUTTON_SUCCESS',
          meta_data: {
            lead_email: encodedEmail,
          },
        });
      })
      .catch(error => {
        setLoading(false);
        setErrorMessage(tryAgainErrorMsg);
        console.log('error', error);
      });
  };

  const trackLeadSubmission = encodedEmail => {
    ZTrackingClient.recordClick({
      ui_source: 'LEAD_GEN_MODAL',
      component:
        insertLeadResponse?.status === leadStatusEnum.LEAD_STATUS_CONTACT_SAVED
          ? 'SUBMIT_BUTTON'
          : 'NEXT_BUTTON',
      meta_data: {
        lead_email: encodedEmail,
      },
    });
  };

  const handleLeadFormSubmit = async () => {
    setErrorMessage('');
    // Reset all error states
    setNameError(false);
    setEmailError(false);
    setPhoneError(false);

    // Check if any field is empty
    if (
      !name ||
      !name.trim() ||
      !email ||
      !email.trim() ||
      !phone ||
      !phone.trim()
    ) {
      if (!name || !name.trim()) setNameError(true);
      if (!email || !email.trim()) setEmailError(true);
      if (!phone || !phone.trim()) setPhoneError(true);

      setErrorMessage(EmptyFieldErrMsg);
      return;
    }

    const isEmailValid = validateLeadEmail(email);
    const isPhoneValid = validatePhoneNumber(phone) && phone.length === 10;

    if (!isEmailValid && !isPhoneValid) {
      setEmailError(true);
      setPhoneError(true);
      setErrorMessage(invalidPhoneEmailErrMsg);
      return;
    }

    if (!isEmailValid) {
      setEmailError(true);
      setErrorMessage(invalidEmailErrMsg);
      return;
    }

    if (!isPhoneValid) {
      setPhoneError(true);
      setErrorMessage(invalidPhoneErrMsg);
      return;
    }

    const encodedEmail = await sha1Encode(email);
    handleInsertLeads(encodedEmail, true); // Pass true for inline form
    trackLeadSubmission(encodedEmail);
  };

  const handleCardClick = e => {
    if (isAnimatingCard) return; // Prevent animation overlap

    const cardId = e?.currentTarget?.id?.split('-')[1];

    if (clickedCard === cardId) {
      // If the clicked card is already open, close it
      setLastClickedCard(cardId);
      setClickedCard(null);
    } else {
      // Otherwise, open the clicked card and close the previously opened card
      setLastClickedCard(clickedCard);
      setClickedCard(cardId);
    }
    setIsAnimatingCard(true);
    ZTrackingClient.recordClick({
      ui_source: 'LANDING',
      component: `USE_CASE_CARD_${cardId}`,
    });
  };

  const marqueeRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const marquee = marqueeRef.current;
    const marqueeWidth = marquee.scrollWidth / 2;

    animationRef.current = anime({
      targets: marquee,
      translateX: [0, -marqueeWidth],
      duration: 70000,
      easing: 'linear',
      loop: true,
    });

    // Cleanup animation on unmount
    return () => {
      if (animationRef.current) animationRef.current.pause();
    };
  }, []);

  // Pause on hover
  const handleMouseEnter = () => {
    if (animationRef.current) animationRef.current.pause();
  };

  // Resume on hover out
  const handleMouseLeave = () => {
    if (animationRef.current) animationRef.current.play();
  };

  useEffect(() => {
    if (clickedCard !== null) {
      // Flip the clicked card open
      anime({
        targets: `#card-${clickedCard}`,
        scale: [{ value: 1 }, { value: 1.05 }, { value: 1, delay: 250 }],
        rotateY: { value: '+=180', delay: 0 },
        easing: 'easeInOutSine',
        duration: 500,
        complete: () => {
          setIsAnimatingCard(false);
        },
      });
    }

    if (lastClickedCard !== null) {
      // Flip the previously clicked card closed
      anime({
        targets: `#card-${lastClickedCard}`,
        scale: [{ value: 1 }, { value: 1.05 }, { value: 1, delay: 250 }],
        rotateY: { value: '0', delay: 0 },
        easing: 'easeInOutSine',
        duration: 500,
        complete: () => {
          setIsAnimatingCard(false);
        },
      });
    }
  }, [clickedCard, lastClickedCard]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      if (window.scrollY > 400) {
        setScrolledMobile(true);
      } else {
        setScrolledMobile(false);
      }

      if (window.scrollY > 560) {
        setInlineFormScrolledUp(true);
      } else {
        setInlineFormScrolledUp(false);
      }
      // Flip the first card when scrolled to a certain position
      if (window.scrollY >= 600 && clickedCard === -1 && !isAnimatingCard) {
        setIsAnimatingCard(true); // Set animation state
        setTimeout(() => {
          setClickedCard(0); // Open the first card
        }, 1000);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [clickedCard, isAnimatingCard]);

  const carouselComponent = val => {
    return (
      <>
        <CarouselDesktopWrapper className='only-desktop'>
          <CheersSlideContainer>
            <CheersSlideImageContainer colD={4} colT={4} colM={12}>
              <img src={val.image} alt={val.title} />
            </CheersSlideImageContainer>
            <CheersSlideContent colD={8} colT={8} colM={12}>
              <p>{val.description}</p>
              <CheersSlideFooter>
                <div>
                  <hr />
                </div>
                <CheersSlideSign>
                  <CheersSlideSignLeft colD={8} colT={6} colM={12}>
                    <H4>{val.name}</H4>
                    <p>{val.designation}</p>
                  </CheersSlideSignLeft>
                  <CheersSlideSignRight colD={4} colT={6} colM={12}>
                    {val.logo}
                  </CheersSlideSignRight>
                </CheersSlideSign>
              </CheersSlideFooter>
            </CheersSlideContent>
          </CheersSlideContainer>
        </CarouselDesktopWrapper>
        <CarouselMobileWrapper className='only-mobile'>
          <CheersSlideContainer>
            <CheersSlideImageContainer colD={4} colT={4} colM={12}>
              <img src={val.image} alt={val.title} />
            </CheersSlideImageContainer>
            <CheersSlideContent colD={8} colT={8} colM={12}>
              <CheersSlideFooter>
                <CheersSlideSign>
                  <CheersSlideSignLeft colD={6} colT={6} colM={7}>
                    <H4>{val.name}</H4>
                    <p>{val.designation}</p>
                  </CheersSlideSignLeft>
                  <CheersSlideSignRight colD={6} colT={6} colM={5}>
                    {val.logo}
                  </CheersSlideSignRight>
                </CheersSlideSign>
                <div>
                  <hr />
                </div>
              </CheersSlideFooter>
              <p>{val.description}</p>
            </CheersSlideContent>
          </CheersSlideContainer>
        </CarouselMobileWrapper>
      </>
    );
  };

  const LeadSubmitPopup = (onClose = closeModal) => {
    return (
      <LeadSuccessWrapper className='lead-success-modal'>
        <IconWrapper colD={12} colT={12} colM={12}>
          <CheckCircleFill size='6rem' color={green.z500} />
        </IconWrapper>
        <Layout.Item colD={12} colT={12} colM={12}>
          <H1>Thank you for sharing the details!</H1>
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <H4>
            We&apos;re eager to connect and explore how our solutions can
            benefit your business 😄
          </H4>
        </Layout.Item>
        <ModalButtonWrapper colD={12} colT={12} colM={12}>
          <Button size='medium' onClick={onClose} loading={loading}>
            Okay
          </Button>
        </ModalButtonWrapper>
      </LeadSuccessWrapper>
    );
  };

  const LeadForm = () => {
    return (
      <LeadFormLayout gap={5}>
        <LeadFormTitle>Let&apos;s get you started!</LeadFormTitle>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Your name'
            innerWidth='100%'
            type='text'
            value={name}
            onChange={handleNameChange}
            onFocus={handleYourNameFocus}
            hasError={nameError}
            leftIcon={(
              <InputIconWrapper>
                <Input1SVG />
              </InputIconWrapper>
            )}
          />
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Work email ID'
            innerWidth='100%'
            type='email'
            value={email}
            onChange={handleEmailChange}
            onFocus={handleWorkEmailFocus}
            hasError={emailError}
            leftIcon={(
              <InputIconWrapper>
                <Input3SVG />
              </InputIconWrapper>
            )}
          />
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Phone number'
            innerWidth='100%'
            type='text'
            value={phone}
            onChange={handlePhoneChange}
            onFocus={handlePhoneNumberFocus}
            hasError={phoneError}
            leftIcon={(
              <InputIconWrapper>
                <Input4SVG />
              </InputIconWrapper>
            )}
          />
        </Layout.Item>
        {errorMessage && (
          <Layout.Item colD={12} colT={12} colM={12}>
            <ErrorContainerV2>{errorMessage}</ErrorContainerV2>
          </Layout.Item>
        )}
        <Layout.Item colD={12} colT={12} colM={12}>
          <Button
            size='large'
            onClick={handleLeadFormSubmit}
            loading={loading}
            style={{ fontSize: '1.6rem' }}
            className={isFormValid() ? 'next-button' : ''} // next-button class is used to track successful leads through GTM and send back feedback to linkedin to improve the ad performance
          >
            Request a Demo
          </Button>
        </Layout.Item>
      </LeadFormLayout>
    );
  };

  const ModalComponent = () => {
    if (insertLeadResponse?.status === leadStatusEnum.LEAD_STATUS_COMPLETED) {
      return LeadSubmitPopup();
    }
    return (
      <Layout gap={5}>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Your name'
            innerWidth='100%'
            type='text'
            value={name}
            onChange={handleNameChange}
            onFocus={handleYourNameFocus}
            hasError={nameError}
            leftIcon={(
              <ModalInputIconWrapper>
                <Input1SVG />
              </ModalInputIconWrapper>
            )}
          />
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Work email ID'
            innerWidth='100%'
            type='email'
            value={email}
            onChange={handleEmailChange}
            onFocus={handleWorkEmailFocus}
            hasError={emailError}
            leftIcon={(
              <ModalInputIconWrapper>
                <Input3SVG />
              </ModalInputIconWrapper>
            )}
          />
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <StyledInput
            label='Phone number'
            innerWidth='100%'
            type='text'
            value={phone}
            onChange={handlePhoneChange}
            onFocus={handlePhoneNumberFocus}
            hasError={phoneError}
            leftIcon={(
              <ModalInputIconWrapper>
                <Input4SVG />
              </ModalInputIconWrapper>
            )}
          />
        </Layout.Item>
        {errorMessage && (
          <Layout.Item colD={12} colT={12} colM={12}>
            <ErrorContainer>
              <H4>{errorMessage}</H4>
            </ErrorContainer>
          </Layout.Item>
        )}
        <ModalButtonWrapper colD={12} colT={12} colM={12}>
          <Button
            size='medium'
            onClick={handleLeadFormSubmit}
            loading={loading}
            className={isFormValid() ? 'next-button' : ''} // next-button class is used to track successful leads through GTM and send back feedback to linkedin to improve the ad performance
          >
            Request a Demo
          </Button>
        </ModalButtonWrapper>
      </Layout>
    );
  };

  return (
    <>
      {noIndex && (
        <Helmet>
          <meta name='robots' content='noindex, nofollow' />
        </Helmet>
      )}
      <StyledModal
        visible={isVisible}
        isCentered
        onClose={closeModal}
        showCloseIcon
        title={
          insertLeadResponse?.status ===
          leadStatusEnum.LEAD_STATUS_COMPLETED ? (
            <></>
          ) : (
            <H1>{modalTitle}</H1>
          )
        }
        isMobileDialog
        padding='2rem 3rem'
        zIndex={100}
        overlayClickClose={0}
      >
        <ModalContainer>{ModalComponent()}</ModalContainer>
      </StyledModal>
      <StyledModal
        visible={isSuccessModalVisible}
        isCentered
        onClose={closeSuccessModal}
        showCloseIcon
        title={<></>}
        isMobileDialog
        padding='2rem 3rem'
        zIndex={100}
        overlayClickClose={0}
      >
        <ModalContainer>{LeadSubmitPopup(closeSuccessModal)}</ModalContainer>
      </StyledModal>
      <PageLayout>
        <StyledNavbarMobile
          scrolled={scrolledMobile}
          className='only-mobile'
          isModalOpen={isVisible}
        >
          <ConnectStickyButton colD={12} colT={6} colM={12}>
            <Button
              size='large'
              onClick={handleConnectNowStickyMobileButton}
              className='connect-now-button'
            >
              Request a Demo
            </Button>
          </ConnectStickyButton>
        </StyledNavbarMobile>
        <Fold1Container rowD={12} rowT={12} rowM={12}>
          <Fold1SVGLeftContainer>
            <Fold1SVGLeft />
          </Fold1SVGLeftContainer>
          <Fold1SVGRightContainer>
            <Fold1SVGRight />
          </Fold1SVGRightContainer>
          <StyledNavbar scrolled={scrolled}>
            <ZLogoWrapper colD={6} colT={6} colM={6}>
              <ZFELogo />
            </ZLogoWrapper>
            {inlineFormScrolledUp && (
              <ConnectButton colD={6} colT={6} colM={6}>
                <Button
                  size='small'
                  onClick={handleConnectNowNavbarClick}
                  className='connect-now-button'
                >
                  Request a Demo
                </Button>
              </ConnectButton>
            )}
          </StyledNavbar>
          <Layout>
            <Fold1Content
              className='only-desktop'
              rowD={12}
              rowT={12}
              rowM={12}
            >
              <Fold1DesktopWrapper>
                <Layout.Item colD={6.5} colT={12} colM={12}>
                  <Fold1TextWrapper>
                    <PopOutWrapper delay={20}>
                      <H1>
                        Managing fo
                        <CrownContainer>
                          o
                          <CrownSVG />
                        </CrownContainer>
                        d
                      </H1>
                      <H1>
                        at work is 
                        {' '}
                        <span className='red'>easier</span>
                      </H1>
                      <H1>than ever</H1>
                    </PopOutWrapper>
                    <FadeInWrapper delay={100}>
                      <H4>
                        ...also, enjoy guaranteed savings&nbsp;
                        <LaughingEmojiSVG />
                      </H4>
                    </FadeInWrapper>
                  </Fold1TextWrapper>
                </Layout.Item>
                <Layout.Item
                  colD={5.5}
                  colT={12}
                  colM={12}
                  className='only-desktop'
                >
                  <Fold1FormWrapper>
                    <FadeInWrapper delay={200}>
                      <InlineFormWrapper>{LeadForm()}</InlineFormWrapper>
                    </FadeInWrapper>
                  </Fold1FormWrapper>
                </Layout.Item>
              </Fold1DesktopWrapper>
            </Fold1Content>
            <Fold1Content className='only-mobile' rowD={12} rowT={12} rowM={12}>
              <PopOutWrapper delay={20}>
                <H1>
                  Managing fo
                  <CrownContainer>
                    o
                    <CrownSVG />
                  </CrownContainer>
                  d
                </H1>
                <H1>
                  at work is&nbsp;
                  <span className='red'>easier</span>
                </H1>
                <H1>than ever</H1>
              </PopOutWrapper>
              <FadeInWrapper delay={0}>
                <H4>
                  ...also, enjoy guaranteed savings&nbsp;
                  <img src={SMILY_IMG} alt='smily' />
                </H4>
                <ConnectNowButton
                  onClick={handleConnectNowClick}
                  size='medium'
                  className='connect-now-button'
                >
                  Request a Demo
                </ConnectNowButton>
                <ArrowMobileSVG />
              </FadeInWrapper>
            </Fold1Content>
            <TestimonialSlidingContainer
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <FadeInWrapper delay={100}>
                <MarqueeWrapper ref={marqueeRef}>
                  {[...Testimonials, ...Testimonials].map(testimonial => (
                    <TestimonialSlider>
                      <TestimonialSlide>
                        <TestimonialLogoContainer colD={4} colT={4} colM={4}>
                          {testimonial.logo}
                        </TestimonialLogoContainer>
                        <TestimonialContent colD={8} colT={8} colM={8}>
                          <H4>{testimonial.title}</H4>
                          <p>{testimonial.comment}</p>
                        </TestimonialContent>
                      </TestimonialSlide>
                    </TestimonialSlider>
                  ))}
                </MarqueeWrapper>
              </FadeInWrapper>
            </TestimonialSlidingContainer>
          </Layout>
        </Fold1Container>
        <Fold2Container id="use-cases" ref={fold2Ref} rowD={12} rowT={12} rowM={12}>
          <Layout>
            <Fold2Heading rowD={12} rowT={12} rowM={12}>
              <FadeInWrapper delay={100}>
                <HeaderPill>USE CASES</HeaderPill>
                <H1>Multiple use cases,</H1>
                <br />
                <H2>One platform</H2>
              </FadeInWrapper>
            </Fold2Heading>
            <Fold2CardsContainer>
              {UseCases.map((useCase, index) => (
                <>
                  <Fold2Cards
                    className='only-desktop'
                    colD={4}
                    colT={6}
                    colM={6}
                  >
                    <FadeInWrapper delay={100}>
                      {useCase.logo}
                      <H4>{useCase.title}</H4>
                      <p>{useCase.description}</p>
                    </FadeInWrapper>
                  </Fold2Cards>
                  <Fold2CardsMobileContainer
                    colD={4}
                    colT={5.8}
                    colM={5.8}
                    onClick={handleCardClick}
                    className='only-mobile'
                    id={`card-${index}`}
                  >
                    <FadeInWrapper delay={100}>
                      <Fold2CardsMobile className='only-mobile front'>
                        <img src={useCase.img} alt={useCase.title} />
                        <H4>{useCase.title}</H4>
                      </Fold2CardsMobile>
                      <Fold2CardsMobile className='only-mobile back'>
                        <p>{useCase.description}</p>
                      </Fold2CardsMobile>
                    </FadeInWrapper>
                  </Fold2CardsMobileContainer>
                </>
              ))}
            </Fold2CardsContainer>
          </Layout>
        </Fold2Container>
        <Fold3Container id="why-us" rowD={12} rowT={12} rowM={12}>
          <Fold3Heading className='only-desktop' rowD={12} rowT={12} rowM={12}>
            <FadeInWrapper delay={100}>
              <HeaderPill>WHY US?</HeaderPill>
              <H1 className='only-desktop'>
                Unparalleled&nbsp;
                <span>convenience</span>
              </H1>
            </FadeInWrapper>
          </Fold3Heading>
          <Fold3HeadingMobile
            className='only-mobile'
            rowD={12}
            rowT={12}
            rowM={12}
          >
            <FadeInWrapper delay={100}>
              <HeaderPill className='only-mobile'>WHY US?</HeaderPill>
              <H1 className='only-mobile'>Unparalleled</H1>
              <span className='only-mobile' style={{ paddingBottom: '23%' }}>
                convenience
              </span>
            </FadeInWrapper>
          </Fold3HeadingMobile>
          <Layout>
            <Fold3Left colD={5.5} colT={12} colM={12}>
              {WhyUs.map(whyUs => (
                <FadeInWrapper delay={100}>
                  <WhyUsCard>
                    <WhyUsCardLeft colD={2} colT={4} colM={3}>
                      {whyUs.svg}
                    </WhyUsCardLeft>
                    <WhyUsCardRight colD={10} colT={8} colM={9}>
                      <H4>{whyUs.title}</H4>
                      <p>{whyUs.description}</p>
                    </WhyUsCardRight>
                  </WhyUsCard>
                </FadeInWrapper>
              ))}
            </Fold3Left>
            <Fold3Right colD={6.5} colT={7} colM={0}>
              <FadeInWrapper delay={100}>
                <img src={WHY_US_IMG} alt='Food at work logo' />
              </FadeInWrapper>
            </Fold3Right>
          </Layout>
          <SavingsContainer id="savings" className='only-desktop'>
            <SavingsHeader rowD={12} rowT={12} rowM={12}>
              <FadeInWrapper delay={100}>
                <H1>
                  Unmatched&nbsp;
                  <span className='red'> savings</span>
                </H1>
              </FadeInWrapper>
            </SavingsHeader>
            <SaingsSVGContainer>
              <SavingsBGSVG />
            </SaingsSVGContainer>
            <FadeInWrapper delay={100}>
              <SavingsCardContainer>
                <SavingsCard colD={5.8} colT={5.5} colM={12}>
                  <div>
                    <Savings1SVG />
                  </div>
                  <H4>On food delivery</H4>
                  <p style={{ paddingLeft: '5%', paddingRight: '5%' }}>
                    Exclusive restaurant discounts & extra savings on orders
                    over ₹3,000
                  </p>
                </SavingsCard>
                <SavingsCard colD={5.8} colT={5.5} colM={12}>
                  <div>
                    <Savings2SVG />
                  </div>
                  <H4>On dining bills</H4>
                  <p style={{ paddingLeft: '5%', paddingRight: '5%' }}>
                    Enjoy discounts of up to 40% at 28,000+ partner restaurants
                  </p>
                </SavingsCard>
              </SavingsCardContainer>
            </FadeInWrapper>
            <VerticalLineSVGContainer>
              <VerticalLineSVG />
            </VerticalLineSVGContainer>
          </SavingsContainer>
          <SavingsContainer className='only-mobile'>
            <SavingsHeader rowD={12} rowT={12} rowM={12}>
              <FadeInWrapper delay={100}>
                <H1>Unmatched</H1>
                <span className='red'> savings</span>
              </FadeInWrapper>
            </SavingsHeader>
            <FadeInWrapper delay={100}>
              <SavingsCardContainer>
                <SavingsCard colD={3.5} colT={5.5} colM={5.8}>
                  <Savings1SVG />
                  <H4>On food delivery</H4>
                  <p>
                    Exclusive restaurant discounts & extra savings on orders
                    over ₹3,000
                  </p>
                </SavingsCard>
                <SavingsCard colD={3.5} colT={5.5} colM={5.7}>
                  <Savings2SVG />
                  <H4>On dining bills</H4>
                  <p>
                    Enjoy discounts of up to 40% at 28,000+ partner restaurants
                  </p>
                </SavingsCard>
              </SavingsCardContainer>
            </FadeInWrapper>
          </SavingsContainer>
        </Fold3Container>
        <Fold4Container id="how-it-works" rowD={12} rowT={12} rowM={12}>
          <Layout>
            <Fold2Heading rowD={12} rowT={12} rowM={12}>
              <FadeInWrapper delay={100}>
                <HeaderPill>HOW IT WORKS</HeaderPill>
                <H1>Manage centrally from</H1>
                <span>a single dashboard</span>
              </FadeInWrapper>
            </Fold2Heading>
            {HowItWorks.map(useCase => (
              <Fold4Cards colD={4} colT={12} colM={12}>
                <FadeInWrapper delay={100}>
                  {useCase.svg}
                  <H4>{useCase.title}</H4>
                  <p style={{ paddingBottom: '1rem' }}>{useCase.description}</p>
                </FadeInWrapper>
              </Fold4Cards>
            ))}
          </Layout>
        </Fold4Container>
        <Fold4SVGContainer className='only-desktop'>
          <FadeInWrapper delay={100}>
            <img src={FLOW_LINE_IMG} alt='flow line' />
          </FadeInWrapper>
        </Fold4SVGContainer>
        <Fold5Container id="dashboard" className='only-desktop'>
          <DashboardViewSVGContainer>
            <FadeInWrapper delay={100}>
              <img src={DASHBOARD_SS} alt='dashboard' />
            </FadeInWrapper>
          </DashboardViewSVGContainer>
          <RedRectangle />
          <WhiteBox />
        </Fold5Container>
        <CheersFoldContainer id="customer-cheers">
          <CommaSVG />
          <SavingsHeader rowD={12} rowT={12} rowM={12}>
            <FadeInWrapper delay={100}>
              <H1>Customer Cheers</H1>
            </FadeInWrapper>
          </SavingsHeader>
        </CheersFoldContainer>
        <CarouselContainer>
          <FadeInWrapper delay={100}>
            <Carousel
              componentData={CustomerCheersData}
              componentToRender={carouselComponent}
              infinite={1}
              isResponsive={1}
              carouselWidth='100%'
              width='100%'
              slidesToShow={1}
              arrowInsetPadding={window.innerWidth < 768 ? '3.5rem' : '6rem'}
              autoScroll={1}
              dots={window.innerWidth > 768}
              autoScrollTimeout={8000}
            />
          </FadeInWrapper>
        </CarouselContainer>
        <Footer id="footer">
          <FooterContent>
            <ZLogoWrapperFooter colD={3} colT={3} colM={12}>
              <img src={FOOD_AT_WORK_LOGO_IMG} alt='footer' />
              <Layout>
                <Layout.Item colD={1.5} colT={6} colM={2}>
                  <Footer1SVG />
                </Layout.Item>
                <Layout.Item colD={1.5} colT={6} colM={2}>
                  <Footer2SVG />
                </Layout.Item>
              </Layout>
            </ZLogoWrapperFooter>
            {footerData.map(footer => (
              <FooterText colD={2} colT={12} colM={12}>
                <H4>{footer.title}</H4>
                {footer.links.map(link => (
                  <a href={link.link}>
                    <p>{link.label}</p>
                  </a>
                ))}
              </FooterText>
            ))}
            <FooterText colD={3} colT={6} colM={12} className='only-desktop'>
              <img id='footer_image' src={FOOTER_IMG_V2} alt='footer' />
            </FooterText>
          </FooterContent>
        </Footer>
      </PageLayout>
    </>
  );
};

Landing.propTypes = {
  scrollToFold: PropTypes.number,
  noIndex: PropTypes.bool,
};

Landing.defaultProps = {
  scrollToFold: 0,
  noIndex: false,
};

export default Landing;
